// BM-H398 - single writer for the wills questionnaire wire payload.
//
// The vendored engine reads `{ couple, submission, mirror_partner? }`. The
// form keeps exactly that shape under `instance.answers` and every section
// component edits it through the helpers below. No other module builds or
// reshapes the envelope. Field shapes follow the P1 fixture schema
// (backend/tests/fixtures/wills-engine/fixture_1_submission_a.json); the
// engine-read fields are exact.

export const SCHEMA_VERSION = "wills-schema@1.3.0";
export const CLAUSE_LIBRARY_VERSION = "wills-engine-bundle sha256 56bd2c51";

export const SECTION_KEYS = [
  "s1_testator",
  "s2_family",
  "s2a_joint_assets",
  "s2b_shares",
  "s3_executors",
  "s4_guardians",
  "s5_structure",
  "s6_legacies",
  "s7_residue",
  "s8_digital",
  "s9_funeral",
  "s10_exclusions",
  "s11_letter_of_wishes",
];

// Short keys the engine reads off `submission.mirror.same_as_a`.
export const MIRROR_SHORT_KEY = {
  s1_testator: "s1",
  s2_family: "s2",
  s2a_joint_assets: "s2a",
  s2b_shares: "s2b",
  s3_executors: "s3",
  s4_guardians: "s4",
  s5_structure: "s5",
  s6_legacies: "s6",
  s7_residue: "s7",
  s8_digital: "s8",
  s9_funeral: "s9",
  s10_exclusions: "s10",
  s11_letter_of_wishes: "s11",
};

export const STRUCTURES = {
  A: "A_absolute_then_children",
  B: "B_ipdi_life_interest",
  C: "C_nrbdt_plus_absolute",
  DISCUSS: "discuss",
};

// BM-H3898 - who a charity is, for the charity gifts clause (C-12). The solicitor approved the five identifier
// types and the rule that an exempt or excepted charity is identified by a CONTROLLED basis (a fixed list plus a
// reference), never by open text. A fee earner then verifies what is entered; the client cannot verify it.
// These lists are pinned to the assembler's own (backend hub/wills_assembly/charity_gifts.py) by a test.
export const CHARITY_JURISDICTIONS = [
  { value: "england_wales", label: "England and Wales" },
  { value: "scotland", label: "Scotland" },
  { value: "northern_ireland", label: "Northern Ireland" },
];

export const CHARITY_IDENTIFIER_TYPES = [
  { value: "england_wales", label: "Registered with the Charity Commission for England and Wales" },
  { value: "scotland_oscr", label: "Registered with the Scottish Charity Regulator (OSCR)" },
  { value: "northern_ireland_ccni", label: "Registered with the Charity Commission for Northern Ireland" },
  { value: "exempt", label: "An exempt charity (not on a register)" },
  { value: "excepted", label: "An excepted charity (not on a register)" },
];

export const CHARITY_CONTROLLED_BASES = [
  { value: "official_reference", label: "An official reference" },
  { value: "parent_body", label: "Its parent body" },
  { value: "governing_document", label: "Its governing document" },
  { value: "other_verified_basis", label: "Another basis a fee earner can verify" },
];

// The two types that have no register number and are identified by a controlled basis instead.
export const CHARITY_CONTROLLED_TYPES = ["exempt", "excepted"];

export function cloneDeep(value) {
  return JSON.parse(JSON.stringify(value));
}

let idCounter = 0;
export function newId(prefix) {
  idCounter += 1;
  const rand = Math.random().toString(36).slice(2, 8);
  return `${prefix}_${Date.now().toString(36)}${idCounter}${rand}`;
}

// BM-H3960 item 32: the country starts EMPTY. It used to be stamped "United Kingdom" on every new address, so a foreign address
// typed into the form went into the Will labelled as a UK one. An empty country with a UK postcode prints no country; a client
// whose address is outside the UK is asked for the country, and the Will is refused if there is neither a country nor a UK
// postcode (backend/hub/wills_assembly/address_country.py). A saved answer keeps whatever country it already has.
export function emptyAddress() {
  return { line1: "", line2: null, town: "", county: "", postcode: "", country: "" };
}

export function emptySection() {
  return { not_known: [] };
}

export function emptyAnswers() {
  return {
    s1_testator: {
      ...emptySection(),
      full_legal_name: "",
      other_names: [],
      address: emptyAddress(),
      date_of_birth: null,
      has_national_insurance_number: null,
      relationship_status: null,
      spouse_person_id: null,
      marriage_date: null,
      divorce_final_date: null,
      pending_proceedings: null,
      has_lpa_in_place: null,
      has_overseas_assets: null,
      earlier_wills: { state: null, instruments: [], pension_nominations: null, life_policy_nominations: null },
      in_contemplation_of_marriage: { engaged: null, intended_spouse_full_name: null, intended_to_survive_marriage: null, expected_date: null },
    },
    s2_family: { ...emptySection(), has_children: null, children: [], deceased_children_with_issue: null, financial_dependants: [] },
    s2a_joint_assets: { ...emptySection(), owns_property: null, property_selections: [] },
    s2b_shares: {
      ...emptySection(),
      has_shares: null,
      holdings: [],
      agricultural_property: { owns: null, approx_value_gbp: null, qualifies_for_apr_confidence: "na", flagged_for_review: false, notes: null },
      follow_ups: { shareholders_agreement: null, cross_option_agreement: null, pre_emption_rights: null, key_person_insurance: null },
    },
    s3_executors: { ...emptySection(), primary: [], substitutes: [], same_as_trustees: null, trustees: [], professional_executor: null },
    s4_guardians: { ...emptySection(), primary: [], substitutes: [], allowance: { permitted: null, cap_gbp: null } },
    s5_structure: { ...emptySection(), chosen_structure: null, has_spouse_or_partner_beneficiary: null, cohabitee_iht_acknowledged: null, rnrb_impact_acknowledged: null, step_provisions_excluded: false },
    s6_legacies: { ...emptySection(), specific: [], pecuniary: [], charity: [], specific_gifts_to_charities: [], age_contingent: { enabled: null, vesting_age: null } },
    s7_residue: { ...emptySection(), survivorship_days: 28, primary_route: { kind: null }, longstop_beneficiary: null },
    s8_digital: { ...emptySection(), include_digital_assets_clause: null, holdings: [], disposition: null, specific_gift_beneficiary_id: null, digital_executor: { appointed: null, person_id: null }, access_memorandum_acknowledged: null },
    s9_funeral: { ...emptySection(), include_funeral_wishes_clause: null, disposition: null, instructions: "", organ_donation_wish: null, anatomical_donation_wish: null, include_donation_clause: null },
    s10_exclusions: { ...emptySection(), detected_excluded: [], intentional: null, reasons: "", name_excluded_in_will_body: null, separate_letter_of_exclusion: null },
    s11_letter_of_wishes: { ...emptySection(), opted_in: null, captured_wishes: { priority_context: "", why_this_structure: "" } },
    s12_declaration: { confirmed_reflects_wishes: false, confirmed_unpressured: false, understands_draft_status: false },
  };
}

export function emptyCapacityGate() {
  return {
    age_18_plus: null,
    free_and_unpressured: null,
    understands_will: null,
    eng_wal_domiciled: null,
    known_capacity_concerns: null,
    hard_stops_triggered: [],
  };
}

export const CAPACITY_GATE_FIELDS = ["age_18_plus", "free_and_unpressured", "understands_will", "eng_wal_domiciled"];

export function capacityGateComplete(gate) {
  return Boolean(gate) && CAPACITY_GATE_FIELDS.every((f) => typeof gate[f] === "boolean");
}

export function emptyPayload({ formInstanceId = "", matterRef = "", clientName = "" } = {}) {
  const ts = new Date().toISOString();
  const coupleId = newId("couple");
  const testator = newPerson({ relationship: "self", full_legal_name: clientName || "" });
  return {
    couple: {
      id: coupleId,
      mode: null,
      people_register: [testator],
      properties_register: [],
      created_at: ts,
      updated_at: ts,
    },
    submission: {
      schema_version: SCHEMA_VERSION,
      clause_library_version: CLAUSE_LIBRARY_VERSION,
      prompt_versions: {},
      submission: {
        id: formInstanceId || newId("sub"),
        created_at: ts,
        submitted_at: null,
        token: "",
        tier: null,
        quoted_fee_gbp: null,
        couple_id: coupleId,
        role: "single",
        testator_person_id: testator.id,
        partner_person_id: null,
        matter_ref: matterRef,
      },
      capacity_gate: emptyCapacityGate(),
      answers: emptyAnswers(),
      mirror: { same_as_a: {}, b_stashed: {}, b_capacity_gate: null },
      admin_overrides: [],
      tier_signals: {},
    },
  };
}

export function isEnginePayload(raw) {
  return Boolean(
    raw && typeof raw === "object" && raw.couple && Array.isArray(raw.couple.people_register)
      && raw.submission && raw.submission.answers && raw.submission.capacity_gate,
  );
}

// Hydrate from `instance.answers`. Anything that is not the engine envelope
// (an empty dict, or the retired demo shape) starts fresh. Missing sections
// and fields are filled so every section component can rely on its object
// existing. This is what makes a save and return survive.
export function ensurePayload(raw, opts = {}) {
  if (!isEnginePayload(raw)) return emptyPayload(opts);
  const p = cloneDeep(raw);
  const blank = emptyAnswers();
  for (const key of Object.keys(blank)) {
    const have = p.submission.answers[key];
    p.submission.answers[key] = have && typeof have === "object" ? { ...blank[key], ...have } : blank[key];
    if (key !== "s12_declaration" && !Array.isArray(p.submission.answers[key].not_known)) {
      p.submission.answers[key].not_known = [];
    }
  }
  p.submission.mirror = { same_as_a: {}, b_stashed: {}, b_capacity_gate: null, ...(p.submission.mirror || {}) };
  if (!Array.isArray(p.submission.admin_overrides)) p.submission.admin_overrides = [];
  if (!Array.isArray(p.couple.properties_register)) p.couple.properties_register = [];
  return p;
}

export function newPerson(fields = {}) {
  return {
    id: newId("per"),
    full_legal_name: "",
    known_as: null,
    relationship: "other",
    date_of_birth: null,
    is_minor_at_capture: false,
    is_minor_at_submission: false,
    has_capacity_concerns: false,
    capacity_concerns_notes: null,
    address: null,
    email: null,
    deceased: false,
    notes: null,
    ...fields,
  };
}

export function newProperty(fields = {}) {
  return {
    id: newId("prp"),
    address: emptyAddress(),
    approx_value_gbp: null,
    mortgage_outstanding: null,
    mortgage_approx_gbp: null,
    is_main_home: false,
    ownership: null,
    joint_form: null,
    title_number: null,
    tic_share_percent: null,
    declaration_of_trust: null,
    co_owners: [],
    type: "residential",
    ...fields,
  };
}

export function isMinorAtDate(dateOfBirth, at = new Date()) {
  if (!dateOfBirth) return false;
  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) return false;
  const eighteenth = new Date(dob.getFullYear() + 18, dob.getMonth(), dob.getDate());
  return at < eighteenth;
}

export function findPerson(payload, personId) {
  return (payload.couple.people_register || []).find((p) => p.id === personId) || null;
}

export function personName(payload, personId) {
  return findPerson(payload, personId)?.full_legal_name || "";
}

export function testatorOf(payload) {
  return findPerson(payload, payload.submission.submission.testator_person_id);
}

export function partnerOf(payload) {
  return findPerson(payload, payload.submission.submission.partner_person_id);
}

export function isMirror(payload) {
  return payload.couple.mode === "mirror";
}

// "Not known" is a first-class answer on every engine-read field. The field
// is nulled so the engine cannot misread it and the field name is recorded on
// the section so the fee earner's derived summary can count it.
export function setNotKnown(section, field, on) {
  const list = new Set(section.not_known || []);
  const next = { ...section };
  if (on) {
    list.add(field);
    next[field] = null;
  } else {
    list.delete(field);
  }
  next.not_known = Array.from(list);
  return next;
}

export function isNotKnown(section, field) {
  return Array.isArray(section?.not_known) && section.not_known.includes(field);
}

export function countNotKnown(payload) {
  let n = 0;
  const a = payload?.submission?.answers || {};
  for (const key of SECTION_KEYS) n += (a[key]?.not_known || []).length;
  const b = payload?.submission?.mirror?.b_stashed || {};
  for (const key of SECTION_KEYS) n += (b[key]?.not_known || []).length;
  return n;
}

export function minorChildren(payload, answers = payload.submission.answers) {
  return (answers.s2_family.children || [])
    .map((c) => findPerson(payload, c.person_id))
    .filter((p) => p && p.is_minor_at_submission === true);
}

// Tier-determining answers, captured and populated. Never priced here.
export function deriveTierSignals(payload) {
  const a = payload.submission.answers;
  const split = payload.submission.mirror_split?.converted === true;
  return {
    couple_mode: split ? "two_single_wills" : payload.couple.mode,
    chosen_structure: a.s5_structure.chosen_structure ?? null,
    business_holdings: (a.s2b_shares.holdings || []).length,
    agricultural_property: a.s2b_shares.agricultural_property?.owns === true,
    eng_wal_domiciled: payload.submission.capacity_gate.eng_wal_domiciled,
    has_overseas_assets: a.s1_testator.has_overseas_assets ?? null,
    relationship_status: a.s1_testator.relationship_status ?? null,
    minor_children: minorChildren(payload).length > 0,
    severance_elected: (a.s2a_joint_assets.property_selections || []).some((s) => s.severance_decision === "sever_as_part_of_instruction"),
  };
}

function normaliseSection(key, sec, payload) {
  const s = { ...sec };
  if (key === "s6_legacies") {
    // C-12 reads `charity`; the engine's charity warnings read the
    // `specific_gifts_to_charities[].recipient` shape. One writer, derived.
    s.specific_gifts_to_charities = (s.charity || []).map((g) => ({
      recipient: {
        type: "charity",
        charity_name: g.charity_name || null,
        // BM-H3898: an exempt or excepted charity is not on a register, so the engine must not warn that its
        // register number is missing. A gift with no type chosen yet is still treated as a registered one.
        is_registered_uk_charity: !CHARITY_CONTROLLED_TYPES.includes(g.identifier_type),
        charity_number_uk: g.registered_number || null,
        restricted_intent_flagged: Boolean(g.purpose && g.purpose.trim()),
        purpose_note: g.purpose || null,
      },
    }));
  }
  if (key === "s7_residue") {
    const structure = payload.submission.answers.s5_structure.chosen_structure;
    // Exactly one residue block may be live; the engine keys its checks off
    // which block is present. Switching structure retires the other blocks.
    if (structure !== STRUCTURES.A) {
      delete s.shape; delete s.beneficiaries; delete s.substitution_rule; delete s.substitute_takers;
      s.primary_route = { kind: null };
    }
    if (structure !== STRUCTURES.B) {
      delete s.life_tenant; delete s.life_tenant_relationship_to_testator; delete s.ipdi;
      delete s.life_interest_terms; delete s.termination_events; delete s.remainder_trusts; delete s.remaindermen;
    }
    if (structure !== STRUCTURES.C) {
      delete s.nrbdt_scope; delete s.discretionary_beneficiary_class; delete s.trustee_powers;
      delete s.accumulation_power; delete s.rnrb_home_planning; delete s.trust_period;
    }
    if (structure === STRUCTURES.A) {
      if (s.substitution_rule === "surviving_class") s.substitute_takers = { class: "issue_of_beneficiaries", per_stirpes: false };
      else if (s.substitution_rule === "per_stirpes") s.substitute_takers = { class: "issue_of_beneficiaries", per_stirpes: true };
      else delete s.substitute_takers;
    }
    if (structure === STRUCTURES.B && s.life_tenant?.person_id) {
      const rel = findPerson(payload, s.life_tenant.person_id)?.relationship;
      s.life_tenant_relationship_to_testator = rel === "spouse" ? "spouse" : rel === "civil_partner" ? "civil_partner" : rel === "partner" ? "unmarried_partner" : "other";
    }
    if (structure === STRUCTURES.C) {
      s.trust_period = { shape: "default_125_years" };
    }
    // BM-H3636: with the other options removed, each of these has ONE supported value and the form states it
    // instead of asking. Filled only when nothing is saved: a saved removed choice is kept as it is (it is flagged
    // to the client and the fee earner, see removedOptions.jsx) and is never overwritten here.
    if (structure === STRUCTURES.B) {
      if (s.ipdi?.commencement == null) s.ipdi = { ...(s.ipdi || {}), commencement: "immediate" };
      if (s.remainder_trusts?.shape == null) s.remainder_trusts = { ...(s.remainder_trusts || {}), shape: "outright_to_named" };
    }
    if (structure === STRUCTURES.C && s.nrbdt_scope?.shape === "nrb_sum_only" && s.nrbdt_scope.surplus_residue_disposition?.route == null) {
      s.nrbdt_scope = { ...s.nrbdt_scope, surplus_residue_disposition: { route: "outright_to_spouse" } };
    }
    // The longstop is the ultimate default; the engine reads both names.
    if (s.longstop_beneficiary?.kind) s.ultimate_default = { type: s.longstop_beneficiary.kind };
    else delete s.ultimate_default;
  }
  return s;
}

// Recompute every derived field the engine reads. Called on every change so
// the payload on the wire is always internally consistent.
export function normalisePayload(payload, at = new Date()) {
  const p = cloneDeep(payload);
  p.couple.people_register = (p.couple.people_register || []).map((person) => ({
    ...person,
    is_minor_at_submission: isMinorAtDate(person.date_of_birth, at),
  }));
  p.couple.updated_at = at.toISOString();
  for (const key of SECTION_KEYS) {
    p.submission.answers[key] = normaliseSection(key, p.submission.answers[key], p);
    if (p.submission.mirror?.b_stashed?.[key]) {
      p.submission.mirror.b_stashed[key] = normaliseSection(key, p.submission.mirror.b_stashed[key], p);
    }
  }
  p.submission.tier_signals = deriveTierSignals(p);
  return p;
}

export function effectivePartnerAnswers(payload) {
  const mirror = payload.submission.mirror || { same_as_a: {}, b_stashed: {} };
  const out = {};
  for (const key of SECTION_KEYS) {
    const short = MIRROR_SHORT_KEY[key];
    out[key] = mirror.same_as_a[short] === false && mirror.b_stashed[key]
      ? mirror.b_stashed[key]
      : payload.submission.answers[key];
  }
  return out;
}

export function hasMirrorPartner(payload) {
  return isMirror(payload) && Object.keys(payload.submission.mirror?.b_stashed || {}).length > 0;
}

// What crosses the wire to the engine. `mirror_partner` rides along whenever
// the second testator has answered anything of their own, so the engine's
// severance-divergence stop and the s7/s8 cross-checks can run.
export function toEnginePayload(payload) {
  const p = normalisePayload(payload);
  const out = { couple: p.couple, submission: p.submission };
  if (hasMirrorPartner(p)) {
    out.mirror_partner = { answers: effectivePartnerAnswers(p) };
  }
  return out;
}

// Section 6.6 / 6.7: a non-administrative divergence takes the instruction
// off the mirror path. Two single wills; the register keeps both people's
// answers so nothing the clients entered is lost.
export function applyMirrorSplit(payload, divergedSections, at = new Date()) {
  const p = cloneDeep(payload);
  p.submission.mirror_split = {
    converted: true,
    diverged_sections: divergedSections,
    at: at.toISOString(),
  };
  p.submission.submission.role = "single";
  return p;
}
